import { OmsInsuranceProvider } from "@redotech/congo-enums/oms-insurance-provider";
import { ServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  CarrierSchema,
  isFedexCarrier,
  isUpsCarrier,
  isUspsCarrier,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  LegacyShippingProviderType,
  ShippingProviderType,
} from "@redotech/redo-fulfillment-carrier-common/shipment";
import {
  findShipmentByTracker,
  FulfillmentGroup,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { ShipmentTrackerStatus } from "@redotech/redo-fulfillment-group-common/outbound-shipment-label";
import {
  ClaimActionSummary,
  UnclaimableReason,
} from "@redotech/redo-fulfillment-group-common/shipment-summary";
import { redoCarrierNameToCarrier } from "@redotech/redo-fulfillment-rate-common/carrier-account";
import { canonicalCarrierAccountId } from "@redotech/redo-fulfillment-rate-common/direct-carrier-alias";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { FedexClaimInfoSchema } from "./carrier-claim-info/fedex";
import { UpsClaimInfoSchema } from "./carrier-claim-info/ups";
import { ShippingClaimEventSchema } from "./shipping-claim-events";

export const DEFAULT_REDO_KEEP_PROPORTION = 0.25;
export const APPROXIMATE_APPROVAL_RATE = 0.68;

export enum ShippingClaimReason {
  LATE = "late",
  DAMAGED = "damaged",
  LOST = "lost",
  STOLEN = "stolen",
}

export enum ShippingClaimType {
  OUTBOUND = "outbound",
  RETURN = "return",
}

export enum ShippingClaimStatus {
  REQUESTED = "requested", // Merchant requested a claim
  SUBMITTED = "submitted", // Redo submitted a claim to the carrier
  CARRIER_UNSUBMITTED_MERCHANT_APPROVED = "carrier_unsubmitted_merchant_approved", // Redo has not submitted the claim to the carrier, but we approved the merchant's claim
  CARRIER_SUBMITTED_MERCHANT_APPROVED = "carrier_submitted_merchant_approved", // Redo submitted the claim to the carrier, and we approved the merchant's claim
  APPROVED = "approved", // Carrier approved the claim
  REJECTED = "rejected", // Carrier rejected the claim
  PENDING_DOCS = "pending_docs", // Carrier needs more docs
}

export const SUBMITTABLE_CLAIM_STATUSES: ShippingClaimStatus[] = [
  ShippingClaimStatus.REQUESTED,
  ShippingClaimStatus.CARRIER_UNSUBMITTED_MERCHANT_APPROVED,
];

export function isSubmittableClaimStatus(status: ShippingClaimStatus): boolean {
  return SUBMITTABLE_CLAIM_STATUSES.includes(status);
}

export const NonTerminalClaimStatuses = [
  ShippingClaimStatus.REQUESTED,
  ShippingClaimStatus.SUBMITTED,
  ShippingClaimStatus.PENDING_DOCS,
  ShippingClaimStatus.CARRIER_UNSUBMITTED_MERCHANT_APPROVED,
  ShippingClaimStatus.CARRIER_SUBMITTED_MERCHANT_APPROVED,
];

// Status displayed in the admin dashboard
export const ShippingClaimStatusToInternalLabel: Record<
  ShippingClaimStatus,
  string
> = {
  [ShippingClaimStatus.REQUESTED]: "Requested",
  [ShippingClaimStatus.SUBMITTED]: "Submitted to carrier",
  [ShippingClaimStatus.CARRIER_UNSUBMITTED_MERCHANT_APPROVED]:
    "Approved merchant claim, still needs carrier claim",
  [ShippingClaimStatus.CARRIER_SUBMITTED_MERCHANT_APPROVED]:
    "Approved merchant claim, submitted claim to carrier",
  [ShippingClaimStatus.APPROVED]: "Approved",
  [ShippingClaimStatus.REJECTED]: "Rejected",
  [ShippingClaimStatus.PENDING_DOCS]: "Pending docs",
};

// Status displayed to the merchant
export const ShippingClaimStatusToMerchantFacingLabel: Record<
  ShippingClaimStatus,
  string
> = {
  [ShippingClaimStatus.REQUESTED]: "Requested",
  [ShippingClaimStatus.SUBMITTED]: "Submitted to carrier",
  [ShippingClaimStatus.PENDING_DOCS]: "Pending documentation",
  [ShippingClaimStatus.CARRIER_UNSUBMITTED_MERCHANT_APPROVED]: "Approved",
  [ShippingClaimStatus.CARRIER_SUBMITTED_MERCHANT_APPROVED]: "Approved",
  [ShippingClaimStatus.APPROVED]: "Approved",
  [ShippingClaimStatus.REJECTED]: "Rejected",
};

// We want merchants to see that we are on top of claims - so after 6 hours, we show "Submitted to carrier"
// We don't actually want to change the status in the backend because we want the admin dash to be clear about the status
export function getMerchantFacingStatus(
  claim: Pick<ShippingClaim, "status" | "submittedAt">,
): ShippingClaimStatus {
  if (claim.status === ShippingClaimStatus.REQUESTED) {
    const sixHoursAgo = new Date(Date.now() - 6 * 60 * 60 * 1000);
    if (claim.submittedAt < sixHoursAgo) {
      return ShippingClaimStatus.SUBMITTED;
    }
  }

  return claim.status;
}

export enum ShippingClaimSource {
  MERCHANT = "merchant",
  REDO = "redo",
}

export const ALL_SHIPPING_CLAIM_SOURCES: readonly ShippingClaimSource[] = [
  ShippingClaimSource.MERCHANT,
  ShippingClaimSource.REDO,
];

/**
 * Proactive claims are hidden until the merchant files the same claim, which a
 * team on auto-claims never does — so enrollment reveals them instead.
 */
export function getMerchantVisibleClaimSources(
  claimSubmission: { enabled?: boolean | null } | null | undefined,
): readonly ShippingClaimSource[] {
  return claimSubmission?.enabled
    ? ALL_SHIPPING_CLAIM_SOURCES
    : [ShippingClaimSource.MERCHANT];
}

export function isClaimHiddenFromMerchant(
  claim: Pick<ShippingClaim, "source">,
  claimSubmission: { enabled?: boolean | null } | null | undefined,
): boolean {
  return !getMerchantVisibleClaimSources(claimSubmission).includes(
    claim.source,
  );
}

export enum ShippingClaimRequestedBy {
  MERCHANT = "merchant",
  PROACTIVE = "proactive",
}

export enum ShippingClaimDenialReason {
  PACKAGE_DELIVERED = "package_delivered",
  INSUFFICIENT_DOCUMENTATION = "insufficient_documentation",
  OUTSIDE_FILING_WINDOW = "outside_filing_window",
  VALUE_NOT_SUBSTANTIATED = "value_not_substantiated",
  PROOF_OF_DELIVERY_MISSING = "proof_of_delivery_missing",
  NOT_COVERED_BY_SERVICE_LEVEL = "not_covered_by_service_level",
  DUPLICATE_CLAIM = "duplicate_claim",
  CLAIM_ALREADY_PAID = "claim_already_paid",
  OTHER = "other",
}

export const ShippingClaimDenialReasonToLabel: Record<
  ShippingClaimDenialReason,
  string
> = {
  [ShippingClaimDenialReason.PACKAGE_DELIVERED]: "Package delivered",
  [ShippingClaimDenialReason.INSUFFICIENT_DOCUMENTATION]:
    "Insufficient documentation",
  [ShippingClaimDenialReason.OUTSIDE_FILING_WINDOW]: "Outside filing window",
  [ShippingClaimDenialReason.VALUE_NOT_SUBSTANTIATED]:
    "Value not substantiated",
  [ShippingClaimDenialReason.PROOF_OF_DELIVERY_MISSING]:
    "Proof of delivery missing",
  [ShippingClaimDenialReason.NOT_COVERED_BY_SERVICE_LEVEL]:
    "Not covered by service level",
  [ShippingClaimDenialReason.DUPLICATE_CLAIM]: "Duplicate claim",
  [ShippingClaimDenialReason.CLAIM_ALREADY_PAID]: "Claim already paid",
  [ShippingClaimDenialReason.OTHER]: "Other",
};

const MERCHANT_VISIBLE_DENIAL_REASONS = new Set<string>([
  ShippingClaimDenialReason.PACKAGE_DELIVERED,
  ShippingClaimDenialReason.INSUFFICIENT_DOCUMENTATION,
  ShippingClaimDenialReason.VALUE_NOT_SUBSTANTIATED,
  ShippingClaimDenialReason.PROOF_OF_DELIVERY_MISSING,
  ShippingClaimDenialReason.NOT_COVERED_BY_SERVICE_LEVEL,
]);

export function toMerchantDenialReason(
  reason: string | null | undefined,
): ShippingClaimDenialReason {
  return (
    Object.values(ShippingClaimDenialReason).find(
      (known) => known === reason && MERCHANT_VISIBLE_DENIAL_REASONS.has(known),
    ) ?? ShippingClaimDenialReason.OTHER
  );
}

const SHIPPING_CLAIM_REFUND_KEY_PREFIX = "shipping-claim-refund:";
const SHIPPING_CLAIM_TAKEOVER_REFUND_KEY_PREFIX =
  "shipping-claim-takeover-refund:";

export function shippingClaimRefundIdempotencyKey(claimId: string): string {
  return `${SHIPPING_CLAIM_REFUND_KEY_PREFIX}${claimId}`;
}

export function shippingClaimTakeoverRefundIdempotencyKey(
  claimId: string,
): string {
  return `${SHIPPING_CLAIM_TAKEOVER_REFUND_KEY_PREFIX}${claimId}`;
}

export function shippingClaimIdFromRefundKey(
  idempotencyKey: string | null | undefined,
): string | null {
  const prefix = [
    SHIPPING_CLAIM_REFUND_KEY_PREFIX,
    SHIPPING_CLAIM_TAKEOVER_REFUND_KEY_PREFIX,
  ].find((candidate) => idempotencyKey?.startsWith(candidate));
  return prefix && idempotencyKey ? idempotencyKey.slice(prefix.length) : null;
}

export const ShippingClaimPhotoSchema = z.object({
  url: z.string().meta({ privacy: privacy.privateUrl }),
  contentType: z.string().meta({ privacy: privacy.nonPii }),
  fileName: z.string().meta({ privacy: privacy.filename }),
  uploadedAt: z.date().meta({ privacy: privacy.nonPii }),
  description: z.string().nullish().meta({ privacy: privacy.text.merchant }),
});

export type ShippingClaimPhoto = z.infer<typeof ShippingClaimPhotoSchema>;

const DhlClaimInfoSchema = z.object({
  affidavit: ShippingClaimPhotoSchema.nullish().meta({
    privacy: privacy.structured,
  }),
});

export const CarrierSpecificClaimInfoSchema = z.object({
  ups: UpsClaimInfoSchema.nullish().meta({ privacy: privacy.structured }),
  fedex: FedexClaimInfoSchema.nullish().meta({ privacy: privacy.structured }),
  dhl: DhlClaimInfoSchema.nullish().meta({ privacy: privacy.structured }),
});
export type CarrierSpecificClaimInfo = z.infer<
  typeof CarrierSpecificClaimInfoSchema
>;

export const ShippingClaimInsurerNoteSchema = z.object({
  createdAt: z.date().meta({ privacy: privacy.nonPii }),
  insurerStatus: z.string().meta({ privacy: privacy.nonPii }),
  message: z.string().meta({ privacy: privacy.text.thirdParty }),
});

export type ShippingClaimInsurerNote = z.infer<
  typeof ShippingClaimInsurerNoteSchema
>;

const BaseShippingClaimSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  team: zExt.objectId().meta({ privacy: privacy.nonPii }),
  teamName: z.string().meta({ privacy: privacy.nonPii }),
  orderName: z.string().meta({ privacy: privacy.nonPii }),
  customerName: z
    .string()
    .nullish()
    .meta({ privacy: privacy.person_name.customer }),
  customerId: z
    .string()
    .nullish()
    .meta({ privacy: privacy.id.personalExternal }),
  type: createEnum(ShippingClaimType, { enumName: "ShippingClaimType" }).meta({
    privacy: privacy.nonPii,
  }),
  reason: createEnum(ShippingClaimReason, {
    enumName: "ShippingClaimReason",
  }).meta({ privacy: privacy.nonPii }),
  status: createEnum(ShippingClaimStatus, {
    enumName: "ShippingClaimStatus",
  }).meta({ privacy: privacy.nonPii }),
  source: createEnum(ShippingClaimSource, {
    enumName: "ShippingClaimSource",
  }).meta({ privacy: privacy.nonPii }),
  requestedBy: createEnum(ShippingClaimRequestedBy, {
    enumName: "ShippingClaimRequestedBy",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  denialReason: createEnum(ShippingClaimDenialReason, {
    enumName: "ShippingClaimDenialReason",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  submittedAt: z.date().meta({ privacy: privacy.nonPii }),
  trackingCode: z.string().meta({ privacy: privacy.id.tracking }),
  publicTrackingUrl: z.string().meta({ privacy: privacy.privateUrl }),
  shipmentId: z.string().meta({ privacy: privacy.nonPii }),
  carrier: CarrierSchema.meta({ privacy: privacy.nonPii }),
  service: ServiceSchema.meta({ privacy: privacy.structured }),
  // Claims filed before the sub-account moved to `shipping_account` still hold
  // the per-account provider type here, so the legacy spelling stays readable
  // until the backfill clears it.
  rateProvider: z
    .union([
      createEnum(RateProvider, { enumName: "RateProvider" }).meta({
        privacy: privacy.nonPii,
      }),
      createEnum(ShippingProviderType, {
        enumName: "ShippingProviderType",
      }).meta({ privacy: privacy.nonPii }),
      createEnum(LegacyShippingProviderType, {
        enumName: "LegacyShippingProviderType",
      }).meta({ privacy: privacy.nonPii }),
    ])
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  upsGhostAccountId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  // The merchant's carrier account (boughtRate.carrier_account_id) when the label
  // was bought on a merchant account; null for Redo-account claims.
  merchantCarrierAccountId: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  firstScanAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  orderProvider: createEnum(Provider, { enumName: "Provider" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  externalOrderLink: z.string().nullish().meta({ privacy: privacy.privateUrl }),
  labelCostToRedo: z.string().meta({ privacy: privacy.nonPii }),
  labelCostToMerchant: z.string().meta({ privacy: privacy.nonPii }),
  goodsValue: z.string().meta({ privacy: privacy.nonPii }),
  insuredValue: z.string().nullish().meta({ privacy: privacy.nonPii }),
  insuranceProvider: createEnum(OmsInsuranceProvider, {
    enumName: "OmsInsuranceProvider",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  carrierClaimNumber: z.string().nullish().meta({ privacy: privacy.nonPii }),
  carrierClaimSubmittedAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  submittedToCarrierAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  refundIssued: z.boolean().meta({ privacy: privacy.nonPii }),
  refundId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  refundedAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  merchantRefundAmount: z.string().nullish().meta({ privacy: privacy.nonPii }),
  carrierRefundAmount: z.string().nullish().meta({ privacy: privacy.nonPii }),
  internalNote: z.string().meta({ privacy: privacy.text.redoInternal }),
  merchantInternalNotes: z
    .string()
    .nullish()
    .meta({ privacy: privacy.text.merchant }),
  photos: z
    .array(ShippingClaimPhotoSchema.meta({ privacy: privacy.structured }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  notes: z.string().nullish().meta({ privacy: privacy.text.mixed }),
  notifiedMerchantOnStatus: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  internalOrderLink: z.string().nullish().meta({ privacy: privacy.privateUrl }),
  createdAt: z.date().meta({ privacy: privacy.nonPii }),
  updatedAt: z.date().meta({ privacy: privacy.nonPii }),
  shipsuranceClaimId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  shipsuranceClaimRetrievalCode: z
    .string()
    .nullish()
    .meta({ privacy: privacy.id.capability }),
  shipsuranceMessage: z
    .string()
    .nullish()
    .meta({ privacy: privacy.text.thirdParty }),
  insurerNotes: z
    .array(ShippingClaimInsurerNoteSchema.meta({ privacy: privacy.structured }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  events: z
    .array(ShippingClaimEventSchema.meta({ privacy: privacy.structured }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  claimInvoiceUrl: z.string().nullish().meta({ privacy: privacy.privateUrl }),
  automationAttempts: z
    .number()
    .default(0)
    .optional()
    .meta({ privacy: privacy.nonPii }),
  lastStatusCheckedAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  // Count of status checks that found no carrier record; once it crosses the
  // threshold the claim is marked statusCheckAbandoned and dropped from checks.
  statusCheckNotFoundCount: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  statusCheckAbandoned: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
  statusCheckRequeueCount: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  redoKeepRevenue: z.number().nullish().meta({ privacy: privacy.nonPii }),
  carrierSpecificInfo: CarrierSpecificClaimInfoSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  // Pre-reroute identity when a claim is moved to another carrier (e.g. UPS→USPS).
  originalCarrier: CarrierSchema.nullish().meta({ privacy: privacy.nonPii }),
  originalTrackingCode: z
    .string()
    .nullish()
    .meta({ privacy: privacy.id.tracking }),
});

export type BaseShippingClaim = z.infer<typeof BaseShippingClaimSchema>;

export const OMSShippingClaimSchema = BaseShippingClaimSchema.extend({
  type: z.literal(ShippingClaimType.OUTBOUND).meta({ privacy: privacy.nonPii }),
  fulfillmentGroupId: zExt.objectId().meta({ privacy: privacy.nonPii }),
});

export type OMSShippingClaim = z.infer<typeof OMSShippingClaimSchema>;

// For whatever other kinds of claims we need to support next (returns I think)
const ReturnShippingClaimSchema = BaseShippingClaimSchema.extend({
  type: z.literal(ShippingClaimType.RETURN).meta({ privacy: privacy.nonPii }),
  returnId: zExt.objectId().meta({ privacy: privacy.nonPii }),
});

export const ShippingClaimSchema = z.union([
  OMSShippingClaimSchema.meta({ privacy: privacy.structured }),
  ReturnShippingClaimSchema.meta({ privacy: privacy.structured }),
]);

export type ShippingClaim = z.infer<typeof ShippingClaimSchema>;

export const CreateOmsShippingClaimSchema = OMSShippingClaimSchema.omit({
  _id: true,
  createdAt: true,
  updatedAt: true,
});

export type CreateOmsShippingClaim = z.infer<
  typeof CreateOmsShippingClaimSchema
>;

export const CreateReturnShippingClaimSchema = ReturnShippingClaimSchema.omit({
  _id: true,
  createdAt: true,
  updatedAt: true,
});

export type CreateReturnShippingClaim = z.infer<
  typeof CreateReturnShippingClaimSchema
>;

export const ShippingClaimSubmissionSchema = z.object({
  fulfillmentGroupId: zExt.objectId().meta({ privacy: privacy.nonPii }),
  reason: createEnum(ShippingClaimReason, {
    enumName: "ShippingClaimReason",
  }).meta({ privacy: privacy.nonPii }),
  photos: z
    .array(ShippingClaimPhotoSchema.meta({ privacy: privacy.structured }))
    .optional()
    .meta({ privacy: privacy.untypedPayload }),
  additionalDetails: z.string().optional().meta({ privacy: privacy.nonPii }),
});

export type ShippingClaimSubmission = z.infer<
  typeof ShippingClaimSubmissionSchema
>;

export const shippingClaimUpdateSchema = z.object({
  shippingClaimId: z.string().meta({ privacy: privacy.nonPii }),
  status: createEnum(ShippingClaimStatus, { enumName: "ShippingClaimStatus" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  carrierRefundAmount: z.string().nullish().meta({ privacy: privacy.nonPii }),
  carrierClaimNumber: z.string().nullish().meta({ privacy: privacy.nonPii }),
  internalNote: z
    .string()
    .nullish()
    .meta({ privacy: privacy.text.redoInternal }),
  merchantInternalNotes: z
    .string()
    .nullish()
    .meta({ privacy: privacy.text.merchant }),
  carrierClaimSubmittedAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  refundIssued: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
  source: createEnum(ShippingClaimSource, { enumName: "ShippingClaimSource" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  requestedBy: createEnum(ShippingClaimRequestedBy, {
    enumName: "ShippingClaimRequestedBy",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  denialReason: createEnum(ShippingClaimDenialReason, {
    enumName: "ShippingClaimDenialReason",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  shipsuranceMessage: z
    .string()
    .nullish()
    .meta({ privacy: privacy.text.thirdParty }),
});

export type ShippingClaimUpdate = z.infer<typeof shippingClaimUpdateSchema>;

export const CARRIER_INSURANCE_THRESHOLD = 100;

export interface EnrolledMerchantCarrierAccount {
  carrierAccountId: string;
  enabled?: boolean;
  loaSignedAt?: Date;
  loaDocumentUrl?: string;
}

export interface ClaimSubmissionSettings {
  enabled?: boolean;
  redoKeepProportion?: number;
  enrolledMerchantCarrierAccounts?: EnrolledMerchantCarrierAccount[];
}

// A merchant carrier-account enrollment as read from a team's claim-submission
// settings, with nullable fields so it accepts both hydrated and raw team docs.
export interface EnrolledCarrierAccountView {
  carrierAccountId: string;
  enabled?: boolean | null;
  loaSignedAt?: Date | null;
}

export function getEnrolledMerchantCarrierAccountIds(
  enrolledAccounts: readonly EnrolledCarrierAccountView[] | null | undefined,
): string[] {
  return (enrolledAccounts ?? [])
    .filter((account) => account.enabled !== false)
    .map((account) => account.carrierAccountId);
}

export function getLoaSignedCarrierAccountIds(
  enrolledAccounts: readonly EnrolledCarrierAccountView[] | null | undefined,
): string[] {
  return (enrolledAccounts ?? [])
    .filter((account) => !!account.loaSignedAt)
    .map((account) => account.carrierAccountId);
}

// UPS/USPS merchant accounts auto-enroll into claim submission. FedEx also
// supports claims but requires a signed LOA first, so it is excluded here.
export function isAutoEnrollableClaimCarrierAccount(
  redoCarrierName: string,
): boolean {
  const carrier = redoCarrierNameToCarrier[redoCarrierName];
  if (!carrier) {
    return false;
  }
  return isUpsCarrier(carrier) || isUspsCarrier(carrier);
}

// Enable every auto-enrollable merchant carrier account, preserving any existing
// enrollment data (e.g. recorded LOA) for accounts already present.
export function withAutoEnrolledCarrierAccounts(
  existing: readonly EnrolledMerchantCarrierAccount[],
  merchantCarrierAccounts: readonly {
    carrierAccountId: string;
    redoCarrierName: string;
  }[],
): EnrolledMerchantCarrierAccount[] {
  const byId = new Map<string, EnrolledMerchantCarrierAccount>(
    existing.map((account) => [account.carrierAccountId, { ...account }]),
  );
  for (const account of merchantCarrierAccounts) {
    if (!isAutoEnrollableClaimCarrierAccount(account.redoCarrierName)) {
      continue;
    }
    byId.set(account.carrierAccountId, {
      ...byId.get(account.carrierAccountId),
      carrierAccountId: account.carrierAccountId,
      enabled: true,
    });
  }
  return [...byId.values()];
}

// FedEx/UPS claims on a merchant-sourced rate pay the merchant directly, so Redo
// bills its cut. Redo-sourced rates, USPS, and insured (Shipsurance) claims use
// the partial-reimbursement payout instead.
export function shouldChargeMerchantCommission({
  carrier,
  merchantCarrierAccountId,
  insuranceProvider,
}: {
  carrier: string;
  merchantCarrierAccountId: string | null | undefined;
  insuranceProvider: OmsInsuranceProvider | null | undefined;
}): boolean {
  if (insuranceProvider || !merchantCarrierAccountId) {
    return false;
  }
  return isFedexCarrier(carrier) || isUpsCarrier(carrier);
}

export enum MerchantAccountAutoClaimPolicy {
  OPT_IN = "opt_in", // claimable only if the account is whitelisted
  AUTO = "auto", // claimable unless the account is blacklisted (opt-out)
  LOA = "loa", // claimable only if whitelisted and a signed LOA is on file
  UNSUPPORTED = "unsupported", // claims not supported for this carrier
}

export function getMerchantAccountAutoClaimPolicy(
  carrier: string,
): MerchantAccountAutoClaimPolicy {
  if (isFedexCarrier(carrier)) {
    return MerchantAccountAutoClaimPolicy.LOA;
  }
  if (isUpsCarrier(carrier) || isUspsCarrier(carrier)) {
    return MerchantAccountAutoClaimPolicy.AUTO;
  }
  return MerchantAccountAutoClaimPolicy.OPT_IN;
}

export interface MerchantAccountClaimPolicyContext {
  autoClaimsEnabled?: boolean;
  whitelistedCarrierAccountIds?: Iterable<string>;
  blacklistedCarrierAccountIds?: Iterable<string>;
  loaSignedCarrierAccountIds?: Iterable<string>;
}

export function getBlacklistedCarrierAccountIds(
  enrolledAccounts: readonly EnrolledCarrierAccountView[] | null | undefined,
): string[] {
  return (enrolledAccounts ?? [])
    .filter((account) => account.enabled === false)
    .map((account) => account.carrierAccountId);
}

export function getMerchantAccountClaimPolicyContext(
  claimSubmission:
    | {
        enabled?: boolean | null;
        enrolledMerchantCarrierAccounts?:
          | readonly EnrolledCarrierAccountView[]
          | null;
      }
    | null
    | undefined,
): MerchantAccountClaimPolicyContext {
  const autoClaimsEnabled = !!claimSubmission?.enabled;
  const enrolledAccounts = claimSubmission?.enrolledMerchantCarrierAccounts;
  return {
    autoClaimsEnabled,
    // The master switch gates the whitelist: with auto-claims off, no
    // opt-in/LOA account is claimable even if individually enrolled.
    whitelistedCarrierAccountIds: autoClaimsEnabled
      ? getEnrolledMerchantCarrierAccountIds(enrolledAccounts)
      : [],
    blacklistedCarrierAccountIds:
      getBlacklistedCarrierAccountIds(enrolledAccounts),
    loaSignedCarrierAccountIds: getLoaSignedCarrierAccountIds(enrolledAccounts),
  };
}

function toStringSet(ids: Iterable<string> = []): Set<string> {
  return ids instanceof Set ? ids : new Set(ids);
}

// Whether claims may be filed on a merchant-owned carrier account, per the
// carrier's policy (opt-in/auto/loa).
export function isMerchantAccountClaimable(
  carrier: string,
  carrierAccountId: string | null | undefined,
  context: MerchantAccountClaimPolicyContext = {},
): boolean {
  if (!carrierAccountId) {
    return false;
  }
  switch (getMerchantAccountAutoClaimPolicy(carrier)) {
    case MerchantAccountAutoClaimPolicy.OPT_IN:
      return toStringSet(context.whitelistedCarrierAccountIds).has(
        carrierAccountId,
      );
    case MerchantAccountAutoClaimPolicy.AUTO:
      return (
        !!context.autoClaimsEnabled &&
        !toStringSet(context.blacklistedCarrierAccountIds).has(carrierAccountId)
      );
    case MerchantAccountAutoClaimPolicy.LOA:
      return (
        toStringSet(context.whitelistedCarrierAccountIds).has(
          carrierAccountId,
        ) &&
        toStringSet(context.loaSignedCarrierAccountIds).has(carrierAccountId)
      );
    case MerchantAccountAutoClaimPolicy.UNSUPPORTED:
      return false;
  }
}

type ClaimFulfillmentGroup = Pick<
  FulfillmentGroup,
  | "shipment"
  | "additionalShipments"
  | "shippingClaimIdsByTrackingNumber"
  | "shippingClaimIds"
>;

export function omitShippingClaimIds<
  T extends Pick<
    FulfillmentGroup,
    "shippingClaimIdsByTrackingNumber" | "shippingClaimIds"
  >,
>(fulfillmentGroup: T, claimIds: ReadonlySet<string>): T {
  if (claimIds.size === 0) {
    return fulfillmentGroup;
  }
  const byTrackingNumber = fulfillmentGroup.shippingClaimIdsByTrackingNumber;
  return {
    ...fulfillmentGroup,
    shippingClaimIds: fulfillmentGroup.shippingClaimIds?.filter(
      (id) => !claimIds.has(id),
    ),
    shippingClaimIdsByTrackingNumber: byTrackingNumber
      ? Object.fromEntries(
          Object.entries(byTrackingNumber).map(([trackingNumber, ids]) => [
            trackingNumber,
            ids.filter((id) => !claimIds.has(id)),
          ]),
        )
      : byTrackingNumber,
  };
}

export function isClaimAlreadySubmitted(
  fulfillmentGroupData: ClaimFulfillmentGroup,
  trackingNumber: string,
): boolean {
  return fulfillmentGroupData.shippingClaimIdsByTrackingNumber
    ? (fulfillmentGroupData.shippingClaimIdsByTrackingNumber[trackingNumber]
        ?.length ?? 0) > 0
    : (fulfillmentGroupData.shippingClaimIds?.length ?? 0) > 0;
}

// Extra insurance above the carrier threshold is filed via shipsurance/easypost.
function shipmentHasExtraInsurance(
  relevantShipment: ReturnType<typeof findShipmentByTracker>,
): boolean {
  const insuredAmount = relevantShipment?.parcel?.insurance?.insuredAmount;
  return !!insuredAmount && insuredAmount > CARRIER_INSURANCE_THRESHOLD;
}

export function ensureCanCreateClaim(
  fulfillmentGroupData: ClaimFulfillmentGroup,
  trackingNumber: string,
  policyContext: MerchantAccountClaimPolicyContext = {},
) {
  if (!fulfillmentGroupData.shipment) {
    return false;
  }

  const relevantShipment = findShipmentByTracker(
    fulfillmentGroupData,
    trackingNumber,
  );

  const boughtRate = relevantShipment?.boughtRate;

  const merchantAccountClaimAllowed =
    !!boughtRate?.is_merchant_carrier_account &&
    isMerchantAccountClaimable(
      boughtRate.carrier,
      canonicalCarrierAccountId(boughtRate),
      policyContext,
    );

  if (
    isClaimAlreadySubmitted(fulfillmentGroupData, trackingNumber) ||
    !boughtRate ||
    (boughtRate.is_merchant_carrier_account &&
      !shipmentHasExtraInsurance(relevantShipment) &&
      !merchantAccountClaimAllowed)
  ) {
    return false;
  }

  return boughtRate;
}

// Blocked ONLY because the label was bought on the merchant's own carrier
// account below the insurance threshold — surfaced as a disabled action.
export function ensureMerchantAccountClaimBlocked(
  fulfillmentGroupData: ClaimFulfillmentGroup,
  trackingNumber: string,
  policyContext: MerchantAccountClaimPolicyContext = {},
): boolean {
  if (!fulfillmentGroupData.shipment) {
    return false;
  }

  const relevantShipment = findShipmentByTracker(
    fulfillmentGroupData,
    trackingNumber,
  );

  const boughtRate = relevantShipment?.boughtRate;
  if (!boughtRate?.is_merchant_carrier_account) {
    return false;
  }

  if (isClaimAlreadySubmitted(fulfillmentGroupData, trackingNumber)) {
    return false;
  }

  const merchantAccountClaimAllowed = isMerchantAccountClaimable(
    boughtRate.carrier,
    canonicalCarrierAccountId(boughtRate),
    policyContext,
  );

  return (
    !shipmentHasExtraInsurance(relevantShipment) && !merchantAccountClaimAllowed
  );
}

export function getShippingClaimInternalLink(
  claim: ShippingClaim,
): string | undefined {
  if (claim.internalOrderLink) {
    return claim.internalOrderLink;
  }
  if (claim.type === ShippingClaimType.RETURN) {
    return `/order-management/returns/all-returns/${claim.returnId}`;
  }
  return undefined;
}

export function groupHasClaimableShipment(
  fulfillmentGroupData: ClaimFulfillmentGroup,
  policyContext: MerchantAccountClaimPolicyContext = {},
) {
  return [
    fulfillmentGroupData.shipment,
    ...(fulfillmentGroupData.additionalShipments ?? []),
  ].some((shipment) =>
    ensureCanCreateClaim(
      fulfillmentGroupData,
      shipment?.tracker?.trackingCode ?? "",
      policyContext,
    ),
  );
}

export function groupClaimBlockedByMerchantAccount(
  fulfillmentGroupData: ClaimFulfillmentGroup,
  policyContext: MerchantAccountClaimPolicyContext = {},
): boolean {
  return [
    fulfillmentGroupData.shipment,
    ...(fulfillmentGroupData.additionalShipments ?? []),
  ].some((shipment) =>
    ensureMerchantAccountClaimBlocked(
      fulfillmentGroupData,
      shipment?.tracker?.trackingCode ?? "",
      policyContext,
    ),
  );
}

export function groupClaimActionSummary(
  fulfillmentGroupData: ClaimFulfillmentGroup,
  policyContext: MerchantAccountClaimPolicyContext = {},
): ClaimActionSummary {
  if (groupHasClaimableShipment(fulfillmentGroupData, policyContext)) {
    return { claimable: true };
  }
  return {
    claimable: false,
    reason: groupClaimBlockedByMerchantAccount(
      fulfillmentGroupData,
      policyContext,
    )
      ? UnclaimableReason.IS_MERCHANT_ACCOUNT
      : null,
  };
}

export function canOnlySubmitThroughRedo(
  rateProvider: ShippingClaim["rateProvider"],
): boolean {
  return rateProvider === RateProvider.UPS;
}

export function findEarliestOmsInTransitDate(
  trackingDetails?:
    | { datetime: Date; status: ShipmentTrackerStatus }[]
    | null
    | undefined,
): Date | null {
  if (!trackingDetails) {
    return null;
  }

  const inTransitEvents = trackingDetails
    .filter((detail) => detail.status === ShipmentTrackerStatus.IN_TRANSIT)
    .sort((a, b) => a.datetime.getTime() - b.datetime.getTime());

  return inTransitEvents.length > 0 && inTransitEvents[0]?.datetime
    ? inTransitEvents[0].datetime
    : null;
}
